import React from "react";
import Header from "../Home/Header";
import Footer from "../Home/Footer";
import AIHero from "./AIHero";
import "./AIAgent.css";
import { motion } from "framer-motion";
import {
  HiOutlineCpuChip,
  HiOutlineChatBubbleLeftRight,
  HiOutlineChartBar,
  HiOutlineArrowPath,
  HiOutlineBolt,
  HiOutlineSquare3Stack3D,
  HiOutlineGlobeAlt,
  HiOutlineClock,
  HiOutlineCommandLine,
  HiOutlineShieldCheck,
} from "react-icons/hi2";
import {
  SiOpenai,
  SiAnthropic,
  SiPython,
  SiNodedotjs,
  SiZapier,
  SiLangchain,
} from "react-icons/si";

const AIAgent = () => {
  const whyUseCards = [
    {
      icon: <HiOutlineArrowPath />,
      title: "Automate Repetitive Tasks",
      desc: "Perform tasks automatically 24/7 without human intervention.",
    },
    {
      icon: <HiOutlineCpuChip />,
      title: "Enhance Decision Making",
      desc: "Analyze data and make smart decisions based on real-time insights.",
    },
    {
      icon: <HiOutlineChatBubbleLeftRight />,
      title: "Boost Customer Engagement",
      desc: "Interact with users in a way that feels natural and helpful.",
    },
    {
      icon: <HiOutlineSquare3Stack3D />,
      title: "Execute Complex Workflows",
      desc: "Automate multi-step business processes with ease.",
    },
  ];

  const whatAreAgentsGrid = [
    { title: "Automate Tasks", icon: <HiOutlineBolt /> },
    { title: "Analyze & Decide", icon: <HiOutlineChartBar /> },
    { title: "Interact with Users", icon: <HiOutlineChatBubbleLeftRight /> },
    { title: "Execute Workflows", icon: <HiOutlineArrowPath /> },
    { title: "Multi-Agent Systems", icon: <HiOutlineSquare3Stack3D /> },
    { title: "AI Web Solutions", icon: <HiOutlineGlobeAlt /> },
  ];

  const services = [
    { name: "OpenAI", logo: <SiOpenai color="#412991" /> },
    { name: "Claude", logo: <SiAnthropic color="#D97757" /> },
    { name: "LangChain", logo: <SiLangchain color="#118D68" /> },
    { name: "AutoGen", logo: <HiOutlineCommandLine color="#2563eb" /> },
    { name: "Python", logo: <SiPython color="#3776AB" /> },
    { name: "Node.js", logo: <SiNodedotjs color="#339933" /> },
    { name: "Zapier", logo: <SiZapier color="#FF4A00" /> },
    { name: "Pinecone", logo: <HiOutlineShieldCheck color="#2563eb" /> },
  ];

  const keyFeatures = [
    { icon: <HiOutlineClock />, title: "24/7 Automation" },
    { icon: <HiOutlineBolt />, title: "Real-Time Decisions" },
    { icon: <HiOutlineSquare3Stack3D />, title: "Multi-Step Execution" },
    { icon: <HiOutlineGlobeAlt />, title: "Scalable Systems" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <div className="ai-agent-page">
      <Header />
      <AIHero />

      {/* SECTION 1: Why Use AI Agents? */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center">
            <h2 className="heading-large">
              Why Use AI Agents for Your Business?
            </h2>
            <p className="subtitle">
              From customer engagement to backend operations, our AI solutions
              help businesses reduce manual effort and scale faster.
            </p>
          </div>
          <motion.div
            className="feature-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {whyUseCards.map((card, index) => (
              <motion.div
                key={index}
                className="feature-card"
                variants={itemVariants}
              >
                <div className="icon-wrapper">{card.icon}</div>
                <h3 className="card-title">{card.title}</h3>
                <p className="card-text">{card.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: What Are AI Agents? */}
      <section className="section-padding" style={{ backgroundColor: "white" }}>
        <div className="container-custom">
          <div className="text-center">
            <h2 className="heading-large">What Are AI Agents?</h2>
            <p className="subtitle">
              AI Agents are intelligent software systems that can act as digital
              team members, working 24/7 to automate tasks, analyze data, and
              help you streamline your business operations.
            </p>
          </div>

          <div className="about-split">
            <motion.div
              className="analytics-card"
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
            >
              <div className="graph-icon">
                <HiOutlineChartBar />
              </div>
              <div className="stat-group">
                <div className="stat-item">
                  <div className="stat-value">70%</div>
                  <div className="stat-label">Automated Efficiency</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">5x Faster</div>
                  <div className="stat-label">Response Time</div>
                </div>
              </div>
              <p className="card-text" style={{ marginTop: "20px" }}>
                Interact with agents to receive smart insights and automated
                responses instantly.
              </p>
            </motion.div>

            <motion.div
              className="mini-cards-grid"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {whatAreAgentsGrid.map((card, index) => (
                <motion.div
                  key={index}
                  className="mini-card"
                  variants={itemVariants}
                >
                  <div className="mini-card-icon">{card.icon}</div>
                  <h4 className="mini-card-title">{card.title}</h4>
                  <p className="card-text">
                    Intelligent solutions for modern business needs.
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 3: Our AI Agent Services */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center">
            <h2 className="heading-large">Our AI Agent Services</h2>
            <p className="subtitle">
              We provide a range of AI agent solutions tailored to your business
              needs, leveraging the best technologies in the industry.
            </p>
          </div>
          <motion.div
            className="services-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                variants={itemVariants}
              >
                <span style={{ fontSize: "24px", display: "flex" }}>
                  {service.logo}
                </span>
                <span className="service-name">{service.name}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SECTION 4: Key Features */}
      <section className="section-padding" style={{ backgroundColor: "white" }}>
        <div className="container-custom">
          <div className="text-center">
            <h2 className="heading-large">Key Features of Our AI Agents</h2>
            <p className="subtitle">
              We leverage modern AI and automation stack to ensure your success.
            </p>
          </div>
          <motion.div
            className="feature-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {keyFeatures.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                variants={itemVariants}
              >
                <div
                  className="icon-wrapper"
                  style={{
                    background: "var(--primary-blue)",
                    width: "48px",
                    height: "48px",
                  }}
                >
                  {feature.icon}
                </div>
                <h3 className="card-title" style={{ fontSize: "1.1rem" }}>
                  {feature.title}
                </h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SECTION 5: Our Process */}
      {/* <section className="section-padding">
        <div className="container-custom">
          <div className="text-center">
            <h2 className="heading-large">Our Process</h2>
            <p className="subtitle">
              A structured approach to building and deploying your custom AI agents.
            </p>
          </div>
     
        </div>
      </section> */}

      <Footer />
    </div>
  );
};

export default AIAgent;
